/**
 * The unique id is a slug the backend validates with this exact pattern, shared by every
 * *user-entered* unique id (Account, Portfolio, ...). It is duplicated here so a bad value
 * is caught before a round trip, not after. A client-derived idempotency key that is never
 * shown to a user — the attachment upload key, the parse-request key — is a different,
 * free-form shape with its own cap, and does not use this module.
 */
export const UNIQUE_ID_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export const UNIQUE_ID_MIN_LENGTH = 3;
export const UNIQUE_ID_MAX_LENGTH = 36;

export function isValidUniqueId(value: string): boolean {
  return UNIQUE_ID_PATTERN.test(value);
}

export type UniqueIdErrorKey = "required" | "length" | "format";

/**
 * Runs the required -> length -> format ladder against a unique id and names the first rule
 * it fails, or null when the value is valid. Each caller owns the translation of the returned
 * key into its own message.
 *
 * The length check still guards the `> UNIQUE_ID_MAX_LENGTH` case even though every caller's
 * input already caps at that length — validating the bound is this function's job regardless
 * of what a given caller's input widget happens to enforce.
 */
export function uniqueIdErrorKey(value: string): UniqueIdErrorKey | null {
  if (!value) return "required";
  if (value.length < UNIQUE_ID_MIN_LENGTH || value.length > UNIQUE_ID_MAX_LENGTH) return "length";
  if (!isValidUniqueId(value)) return "format";
  return null;
}

/**
 * Derive a valid unique id from a display name.
 *
 * Collapsing every *run* of non-alphanumerics into a single hyphen is what guarantees the
 * `(-[a-z0-9]+)*` half of the pattern: a hyphen can never double, and can never sit next to a
 * separator. Accents are folded (Ação becomes acao); characters NFD does not decompose are
 * dropped by the same rule rather than transliterated, since a half-complete table is worse than
 * a visible, editable field.
 *
 * Returns an empty string when nothing usable survives, so the field is never pre-filled with a
 * value the backend would reject.
 */
export function uniqueIdFromName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, UNIQUE_ID_MAX_LENGTH)
    .replace(/-+$/, '');
}

/**
 * Derives a unique id from a display name and names the first validation rule the result
 * fails, or null when it is valid. `errorKey` is always null while `name` is empty, so an
 * untouched form never reports an error for a value the user has not supplied yet.
 */
/**
 * Translate an error key into a message. `length` and `format` restate the same rule for every
 * resource, so they are shared; only the `required` message names the resource, which is why its
 * key is the caller's to supply.
 */
export function uniqueIdErrorMessage(
  translate: (key: string) => string,
  key: UniqueIdErrorKey | null,
  requiredKey: string,
): string {
  if (key === "required") return translate(requiredKey);
  if (key === "length") return translate("uniqueId.length");
  if (key === "format") return translate("uniqueId.format");
  return "";
}

export function deriveUniqueId(name: string): { derived: string; errorKey: UniqueIdErrorKey | null } {
  const derived = uniqueIdFromName(name);
  const errorKey = name.trim() ? uniqueIdErrorKey(derived) : null;
  return { derived, errorKey };
}
